import React, {useState, useRef, useEffect} from "react";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {ModalFooter} from "../Components/BookingModalShell.jsx";
import {flightService} from "@/services/flightService";
import {BOOKING_CONSTRAINTS, POLICY_CONSTANTS} from "@/constants/booking";
import {isNearAirport} from "@/utils/airports";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {round1, formatBufferApprox} from "@/utils/policyFormat";
import {LiabilityDisclaimerContent} from "./liabilityDisclaimer.jsx";
import {airportContext, isFlightMismatch, flightMismatchMessage, isFlightCancelled, resolveMovementTime, getRecommendedTime, getSmartTimeError, formatSmartTime, formatSmartDateLabel} from "@/utils/smartTime";
import FlightMismatchWarning from "@/customComponents/sharedComponents/smartTime/FlightMismatchWarning.jsx";
import FlightCancelledWarning from "@/customComponents/sharedComponents/smartTime/FlightCancelledWarning.jsx";
import FlightUnavailableWarning from "@/customComponents/sharedComponents/smartTime/FlightUnavailableWarning.jsx";
import TimeErrorWarning from "@/customComponents/sharedComponents/smartTime/TimeErrorWarning.jsx";
import ToAirportRecommendedCard from "@/customComponents/sharedComponents/smartTime/ToAirportRecommendedCard.jsx";
import {waitingItemImage} from "@/customComponents/sharedComponents/smartTime/waitingIcons";
import {
    bigClose,
    boldCar,
    shieldProtected,
    infoIcon,
    arrRight,
    selectedIcon,
    planeTakeoffBold,
    clockIconBold,
    stopWatchBold,
    cardBold,
    plane2dIcon,
} from "@img/index.js";
import {airlineLogo} from "@/utils/airlineLogo.js";
import {rideDateYMD} from "@/utils/dateUtils.js";

export function SmartTimeSelect(props) {
    const [flightNumber, setFlightNumber] = useState(storage.get(STORAGE_KEYS.FLIGHT_NUMBER) ?? '');
    const [flightDetails, setFlightDetails] = useState(() => storage.get(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, null));
    const [flightLoading, setFlightLoading] = useState(false);
    const [flightError, setFlightError] = useState('');
    // Airport mismatch: the looked-up flight's airport ≠ the selected pickup/dropoff
    // airport. Until the user clicks "Proceed anyway" we hide the recommended-pickup
    // block AND withhold the flight from storage, so a Back/reload never commits a
    // mismatched flight. `acknowledged` seeds from a stored flight number (only
    // written on a match or after Proceed anyway).
    const [acknowledged, setAcknowledged] = useState(() => !!storage.get(STORAGE_KEYS.FLIGHT_NUMBER));
    const [proceeding, setProceeding] = useState(false);
    // Last lookup couldn't reach the flight provider (network/HTTP failure, not a clean
    // "no such flight"). Shows the FlightUnavailableWarning card; not persisted.
    const [providerUnavailable, setProviderUnavailable] = useState(false);
    const [dateError, setDateError] = useState('');
    const [timeSelected, setTimeSelected] = useState(storage.get(STORAGE_KEYS.SMART_TIME_SELECTED) === '1');
    const [toAirportExpanded, setToAirportExpanded] = useState(false);
    const [recommendedDetailsPopup, setRecommendedDetailsPopup] = useState(false);
    // Liability Disclaimer info popup (the "i" next to "~3 h before departure").
    // Desktop opens the inline .upper-modal below; mobile routes to the
    // liabilityDisclaimer overlay step (POPUP_STEPS).
    const [liabilityPopup, setLiabilityPopup] = useState(false);
    // fromAirport pickup sections behave as a radio group — user picks a section, then triggers its action.
    // Persisted so returning to this step preserves the user's prior pick (e.g. after going to travelingDateTime and back).
    const [selectedSection, setSelectedSection] = useState(() => {
        const stored = storage.get(STORAGE_KEYS.SMART_TIME_SECTION);
        return stored === 'custom' ? 'custom' : 'recommended';
    });
    useEffect(() => {
        storage.set(STORAGE_KEYS.SMART_TIME_SECTION, selectedSection);
    }, [selectedSection]);
    const isRecommendedActive = selectedSection === 'recommended';
    const isCustomActive = selectedSection === 'custom';
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' && window.innerWidth <= 575
    );
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 575);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const flightInputRef = useRef(null);
    const flightDetailsRef = useRef(null);

    // Destinations layout: [pickup, dropoff, ...stopovers] — dropoff is index 1, not last
    const destinations = storage.get(STORAGE_KEYS.DESTINATIONS, []);
    const firstDest = destinations[0];
    const lastDest = destinations[1];
    // Shared airport/recommendation logic (utils/smartTime). `isNearAirport` is the single
    // canonical detector (the /config/airports list) — same one the edit flow uses. The
    // flight is passed in so the cushion and the breakdown rows come from THIS airport's
    // waiting_times bucket for the right flight scope.
    const {isFromAirport, selectedAirportCode, airportBuffer, waitingItems, arriveEarlyMinutes} =
        airportContext(firstDest, lastDest, isNearAirport, flightDetails);
    const checkMismatch = (fd) => isFlightMismatch(fd, {isFromAirport, selectedAirportCode});
    const flightMismatch = checkMismatch(flightDetails);
    const flightMismatchMsg = flightMismatchMessage(flightDetails, {isFromAirport, selectedAirportCode});
    const flightCancelled = isFlightCancelled(flightDetails);
    // Either condition gates the recommended-pickup block behind "Proceed anyway".
    const flightBlocked = flightMismatch || flightCancelled;

    // Effective departure/arrival times for the flight card: when a flight's time
    // changes (official revisedTime or estimated predictedTime) we show the new time
    // as the primary value and the original struck-through after it.
    const depTime = resolveMovementTime(flightDetails?.departure);
    const arrTime = resolveMovementTime(flightDetails?.arrival);

    const handleFlightLookup = async (e) => {
        e.preventDefault();
        if (!flightNumber.trim()) return;

        const storedRideDate = storage.get(STORAGE_KEYS.RIDE_DATE);
        if (!storedRideDate) {
            setDateError('Please select the date first');
            return;
        }

        setFlightLoading(true);
        setFlightDetails(null);
        setFlightError('');
        setProviderUnavailable(false);
        setDateError('');

        const dateParam = rideDateYMD(storedRideDate);

        const result = await flightService.getFlightDetails(flightNumber, dateParam, isFromAirport ? 'Arrival' : 'Departure');
        setFlightLoading(false);

        if (!result.success) {
            if (result.unavailable) {
                // Provider unreachable → warning card + "Proceed anyway", not dead error text.
                setProviderUnavailable(true);
            } else {
                setFlightError(result.error || "Failed to fetch flight details");
            }
            return;
        }

        if (result.data.length > 0) {
            const fd = result.data[0];
            const blocked = checkMismatch(fd) || isFlightCancelled(fd);
            // The card + error render from React state, but on a MISMATCH or a
            // CANCELLED flight we persist NOTHING — neither the flight number nor the
            // flight details — until the user clicks "Proceed anyway". So a reload (or
            // Back) on an un-acknowledged block starts clean: nothing about the wrong
            // or cancelled flight is saved.
            if (blocked) {
                storage.remove(STORAGE_KEYS.FLIGHT_NUMBER);
                storage.remove(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
                setAcknowledged(false);
            } else {
                storage.set(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, fd);
                storage.set(STORAGE_KEYS.FLIGHT_NUMBER, flightNumber);
                setAcknowledged(true);
            }
            setProceeding(false);
            setFlightDetails(fd);
            setTimeSelected(false);
            storage.remove(STORAGE_KEYS.SMART_TIME_SELECTED);

            if (window.innerWidth <= 575) {
                setTimeout(() => {
                    flightDetailsRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
                }, 200);
            }
        }
    };

    // Mismatch/cancellation acknowledged: commit the flight number + details and
    // reveal the recommended pickup block. No real async work — the brief spinner just
    // acknowledges the click before the block appears.
    const handleProceedAnyway = () => {
        setProceeding(true);
        setTimeout(() => {
            storage.set(STORAGE_KEYS.FLIGHT_NUMBER, flightNumber);
            storage.set(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, flightDetails);
            setAcknowledged(true);
            setProceeding(false);
        }, 500);
    };

    // "Proceed anyway" on the provider-unavailable card. There are NO flight details, so
    // no smart pickup can be computed (the recommended/custom UI is gated behind `calc`).
    // Keep the typed flight number and send the user to manual time entry instead.
    const handleProceedUnavailable = () => {
        setProceeding(true);
        setTimeout(() => {
            storage.set(STORAGE_KEYS.FLIGHT_NUMBER, flightNumber);
            setAcknowledged(true);
            setProceeding(false);
            if (isMobile) storage.set(STORAGE_KEYS.TDT_SHOW_WAITING_INFO, true);
            props.setStep("travelingDateTime");
        }, 500);
    };

    const formatTime = formatSmartTime;
    const formatDateLabel = formatSmartDateLabel;

    const calc = getRecommendedTime({
        flightDetails,
        isFromAirport,
        airportBuffer,
        travelMinutes: storage.get(STORAGE_KEYS.ROUTE_BREAKDOWN, null)?.duration_minutes || 0,
    });
    const confirmDateTimeStr = calc
        ? `${formatTime(calc.recommendedTime)}, ${formatDateLabel(calc.recommendedTime)}`
        : '';
    const timeError = getSmartTimeError(calc?.recommendedTime);
    const isEditMode = storage.get(STORAGE_KEYS.EDIT_STEP) === true;

    const handleSelectTime = () => {
        if (!calc) return;
        setTimeSelected(true);
        storage.set(STORAGE_KEYS.SMART_TIME_SELECTED, '1');
        storage.set(STORAGE_KEYS.RIDE_TIME, calc.recommendedTime.toString());
        if (!isEditMode) props.setStep("selectedCarDetails");
    };

    const handleDone = () => props.setStep("orderDetails");

    // "~3 h before departure" info icon → Liability Disclaimer (dual-mode).
    const openLiability = () => {
        if (isMobile) props.setStep("liabilityDisclaimer");
        else setLiabilityPopup(true);
    };

    return (
        <>
            {/* Recommended pickup details popup (fromAirport, desktop) */}
            {isFromAirport && calc && (
                <div
                    className="upper-modal recommendedPickupDetails"
                    style={{display: recommendedDetailsPopup ? 'flex' : 'none'}}
                >
                    <div className="upper-modal-inner">
                        <ModalHeader
                            inline={true}
                            title=""
                            extraClasses=""
                            totalSteps={0}
                            cancelButton={true}
                            cancelButtonType="icon"
                            backStep={null}
                            showBackButton={false}
                            setStep={props.setStep}
                            closeModal={() => setRecommendedDetailsPopup(false)}
                        />
                        <div className="upper-modal-content padding-bottom-30">
                            <div className="hopn-pickup-options to-airport modalPages-spacing">
                                <div className="hopn-pickup-options-grid">
                                    <div className="hopn-pickup-options-grid-col">
                                        <div className="hopn-pickup-option recommended selected expanded">
                                            <div className="recommendation-tabs">
                                                <div className="recommendation-list">
                                                    <span><img src={boldCar} alt=""/></span>
                                                    <div className="recommendation-content">
                                                        <div className="recommendation-content-list">
                                                            <h3>Recommended pickup <span>{formatTime(calc.recommendedTime)}</span></h3>
                                                        </div>
                                                        <div className="recommendation-content-data">
                                                            <h6>No waiting charges</h6>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="recommendation-content-data">
                                                    <div className="hopn-why-pickup-time">
                                                        <div className="hopn-why-pickup-time-icon">
                                                            <img src={shieldProtected} alt=""/>
                                                        </div>
                                                        <div className="hopn-why-pickup-time-text">
                                                            <h6>Why this pickup time?</h6>
                                                            <p>Your driver monitors your flight and adjusts pickup time automatically</p>
                                                        </div>
                                                    </div>
                                                    {/* The airport's own waiting_times items — labels and
                                                        minutes both from /config/airports, so they always
                                                        add up to the total below. */}
                                                    <ul className="hopn-pickup-breakdown-list">
                                                        {waitingItems.map((item, i) => (
                                                            <li key={i}>
                                                                <span className="hopn-breakdown-icon"><img src={waitingItemImage(item.name)} alt=""/></span>
                                                                <span className="hopn-breakdown-label">{item.name}</span>
                                                                <span className="hopn-breakdown-value">~{formatBufferApprox(item.minutes)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <p className="hopn-pickup-total">
                                                        <span className="hopn-pickup-total-label"><strong>Total</strong> estimated time at airport:</span>
                                                        <span className="hopn-pickup-total-value">
                                                            ~{formatBufferApprox(airportBuffer)} before departure
                                                            <button type="button" className="hopn-info-icon" onClick={openLiability}>
                                                                <img src={infoIcon} alt="Liability disclaimer"/>
                                                            </button>
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="upper-modal-footer modalPages-spacing padding-bottom-30 recommendedPickupDetails-actions">
                            <button
                                type="button"
                                className="hopn-add-passenger-button duble-title"
                                disabled={!!timeError}
                                onClick={() => {
                                    setRecommendedDetailsPopup(false);
                                    handleSelectTime();
                                }}
                            >
                                <div className="multiple-text">
                                    <span>Confirm pickup time</span>
                                    <span>{confirmDateTimeStr}</span>
                                </div>
                            </button>

                            <button
                                type="button"
                                className="hopn-choose-own-time-link"
                                onClick={() => {
                                    setRecommendedDetailsPopup(false);
                                    setSelectedSection('custom');
                                    if (isMobile) {
                                        storage.set(STORAGE_KEYS.TDT_SHOW_WAITING_INFO, true);
                                    }
                                    props.setStep("travelingDateTime");
                                }}
                            >
                                I'll choose my own time
                                <img src={arrRight} alt=""/>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Liability Disclaimer info popup (desktop). Mobile uses the
                liabilityDisclaimer overlay step instead (see openLiability). */}
            <div className="upper-modal liabilityDisclaimer" style={{display: liabilityPopup ? 'flex' : 'none'}}>
                <div className="upper-modal-inner">
                    <ModalHeader
                        inline={true}
                        title=""
                        extraClasses=""
                        totalSteps={0}
                        cancelButton={true}
                        cancelButtonType="icon"
                        backStep={null}
                        showBackButton={false}
                        setStep={props.setStep}
                        closeModal={() => setLiabilityPopup(false)}
                    />
                    <div className="upper-modal-content padding-bottom-30">
                        <div className="hopn-policies-disclaimer modalPages-spacing">
                            <LiabilityDisclaimerContent/>
                        </div>
                    </div>
                </div>
            </div>

            <ModalHeader
                title="Smart Time Select"
                extraClasses=""
                totalSteps={0}
                cancelButton={false}
                cancelButtonType="text"
                backStep={"travelingDateTime"}
                showBackButton={true}
                enableEditModeOnBackButton={false}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />

            <div className={`modalPages-spacing${isEditMode ? '' : ' padding-bottom-30'}`}>
                <div className="hopn-flight-manual hopn-smart-time-select">
                    {/* Flight Number Input */}
                    <div className="hopn-flight-input-main" ref={flightInputRef}>
                        <h2 className="hopn-flight-title">Enter Flight Number</h2>

                        <form
                            className="hopn-flight-input-wrap"
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (flightDetails) {
                                    setFlightDetails(null);
                                    setFlightNumber('');
                                    setFlightError('');
                                    setTimeSelected(false);
                                    setAcknowledged(false);
                                    setProceeding(false);
                                    storage.remove(STORAGE_KEYS.FLIGHT_NUMBER);
                                    storage.remove(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
                                    storage.remove(STORAGE_KEYS.SMART_TIME_SELECTED);
                                    return;
                                }
                                handleFlightLookup(e);
                            }}
                        >
                            <input
                                type="text"
                                name="flight_number"
                                autoComplete="off"
                                value={flightNumber}
                                placeholder="E.g. AC4567"
                                required
                                onChange={(e) => {
                                    if (flightDetails) {
                                        setFlightDetails(null);
                                        setFlightError('');
                                        setAcknowledged(false);
                                        setProceeding(false);
                                        storage.remove(STORAGE_KEYS.FLIGHT_NUMBER);
                                        storage.remove(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
                                    }
                                    if (providerUnavailable) {
                                        setProviderUnavailable(false);
                                        setAcknowledged(false);
                                    }
                                    setFlightNumber(e.target.value);
                                }}
                            />
                            <button type="submit">
                                {flightDetails ? <img src={bigClose} alt="Remove"/> : 'Add'}
                            </button>
                        </form>

                        {dateError && (<div className="error">{dateError}</div>)}
                    </div>
                </div>

                {flightLoading && (
                    <div className="loading"><Loader/></div>
                )}

                {flightError && (<div className="error">{flightError}</div>)}

                {/* Flight Details Card */}
                {flightDetails && (
                    <div className="hopn-airlines-cards hopn-airlines" ref={flightDetailsRef}>
                        <div className="hopn-airlines-card">
                            <div className="hopn-airlines-card-top">
                                {airlineLogo(flightDetails.airline?.name) && (
                                    <div className="country_icon">
                                        <img className="hopn-airline-flag"
                                             src={airlineLogo(flightDetails.airline?.name)}
                                             alt={flightDetails.airline?.name || ''}/>
                                    </div>
                                )}
                                {flightDetails.airline?.name || flightDetails.number}
                                {flightDetails.aircraft?.model && <span>– {flightDetails.aircraft.model}</span>}
                            </div>
                            <div className="hopn-airlines-card-body">
                                <div className="hopn-airlines-from">
                                    <span className="hopn-airlines-city">
                                        {flightDetails.departure?.airport?.municipalityName || flightDetails.departure?.airport?.name || '—'} {flightDetails.departure?.airport?.iata ? `(${flightDetails.departure.airport.iata})` : ''}
                                    </span>
                                    <span className="hopn-airlines-time">
                                        {depTime.timeChanged ? (
                                            <>
                                                <span className="hopn-airlines-time-new">{flightService.formatFlightTime(depTime.effective)}</span>
                                                <span className="hopn-airlines-time-old">{flightService.formatFlightTime(depTime.scheduled)}</span>
                                            </>
                                        ) : (
                                            depTime.scheduled ? flightService.formatFlightTime(depTime.scheduled) : '—'
                                        )}
                                    </span>
                                    <span className="hopn-airlines-date">
                                        {depTime.dateChanged ? (
                                            <>
                                                <span className="hopn-airlines-time-new">{flightService.formatFlightDate(depTime.effective)}</span>
                                                <span className="hopn-airlines-time-old">{flightService.formatFlightDate(depTime.scheduled)}</span>
                                            </>
                                        ) : (
                                            depTime.effective ? flightService.formatFlightDate(depTime.effective) : ''
                                        )}
                                    </span>
                                </div>
                                <div className="hopn-airlines-icon">
                                    <img decoding="async" src={plane2dIcon}/>
                                </div>
                                <div className="hopn-airlines-to">
                                    <span className="hopn-airlines-city">
                                        {flightDetails.arrival?.airport?.municipalityName || flightDetails.arrival?.airport?.name || '—'} {flightDetails.arrival?.airport?.iata ? `(${flightDetails.arrival.airport.iata})` : ''}
                                    </span>
                                    <span className="hopn-airlines-time">
                                        {arrTime.timeChanged ? (
                                            <>
                                                <span className="hopn-airlines-time-new">{flightService.formatFlightTime(arrTime.effective)}</span>
                                                <span className="hopn-airlines-time-old">{flightService.formatFlightTime(arrTime.scheduled)}</span>
                                            </>
                                        ) : (
                                            arrTime.scheduled ? flightService.formatFlightTime(arrTime.scheduled) : '—'
                                        )}
                                    </span>
                                    <span className="hopn-airlines-date">
                                        {arrTime.dateChanged ? (
                                            <>
                                                <span className="hopn-airlines-time-new">{flightService.formatFlightDate(arrTime.effective)}</span>
                                                <span className="hopn-airlines-time-old">{flightService.formatFlightDate(arrTime.scheduled)}</span>
                                            </>
                                        ) : (
                                            arrTime.effective ? flightService.formatFlightDate(arrTime.effective) : ''
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Airport mismatch soft-validation — flight card stays visible; the
                    recommended-pickup block below is withheld until "Proceed anyway". */}
                <FlightMismatchWarning flightMismatch={flightMismatch} message={flightMismatchMsg} acknowledged={acknowledged} proceeding={proceeding} onProceed={handleProceedAnyway}/>

                {/* Cancelled flight soft-validation — same acknowledge-gate as the
                    mismatch. When a flight is BOTH mismatched and cancelled the mismatch
                    owns the single proceed button (showProceed={false} here). */}
                <FlightCancelledWarning flightCancelled={flightCancelled} acknowledged={acknowledged} proceeding={proceeding} onProceed={handleProceedAnyway} showProceed={!flightMismatch}/>

                {/* Provider unreachable — "Fail to get the details from server" + Proceed
                    anyway. No flight card/recommendation possible; proceeding keeps the
                    typed number and routes to manual time entry. */}
                <FlightUnavailableWarning show={providerUnavailable} acknowledged={acknowledged} proceeding={proceeding} onProceed={handleProceedUnavailable}/>

                {/* Calculated Ride Time */}
                {calc && (!flightBlocked || acknowledged) && (
                    <div className="hopn-smart-time-result">
                        {isMobile && isFromAirport && (
                            <h3 className="hopn-pickup-options-title outer">How would you like to set your pickup time?</h3>
                        )}

                        <TimeErrorWarning timeError={timeError}/>

                        {isFromAirport ? (
                            <div className="hopn-pickup-options from-airport">
                                <div className="hopn-pickup-options-grid">
                                    <div className="hopn-pickup-options-grid-col">
                                        <div
                                            className={`hopn-pickup-option recommended ${isRecommendedActive ? 'selected' : 'section-disabled'}`}
                                            onClick={!isRecommendedActive ? () => setSelectedSection('recommended') : undefined}
                                        >
                                            {!isMobile && (
                                                <>
                                                    <h3 className="hopn-pickup-options-title">How would you like to set your pickup time?</h3>
                                                    <p className="hopn-pickup-options-subtitle">Choose the option that works best for you</p>
                                                </>
                                            )}
                                            <div className="hopn-pickup-option-top">
                                                <span className="hopn-pickup-option-tag">RECOMMENDED</span>
                                                <button
                                                    type="button"
                                                    className="hopn-pickup-option-best section-radio"
                                                    onClick={() => setSelectedSection('recommended')}
                                                    aria-pressed={isRecommendedActive}
                                                    aria-label="Select recommended pickup"
                                                >
                                                    <h6>Best choice</h6>
                                                    <img src={selectedIcon} alt=""/>
                                                </button>
                                            </div>
                                            <div className="recommendation-tabs">
                                                <button
                                                    type="button"
                                                    className="recommendation-list"
                                                    disabled={!isRecommendedActive || !!timeError}
                                                    onClick={() => {
                                                        if (isMobile) {
                                                            props.setStep("recommendedPickupDetails");
                                                            return;
                                                        }
                                                        setRecommendedDetailsPopup(true);
                                                    }}
                                                >
                                                    <span><img src={boldCar} alt=""/></span>
                                                    <div className="recommendation-content">
                                                        <div className="recommendation-content-list">
                                                            <h3>Recommended pickup <span>{formatTime(calc.recommendedTime)}</span></h3>
                                                            <img src={arrRight} alt=""/>
                                                        </div>
                                                        <div className="recommendation-content-data">
                                                            <h6>No waiting charges</h6>
                                                            <div className="recommendation-content-bottom">
                                                                <p>Your driver arrives at the perfect time, calculated based on your flight and traffic</p>
                                                                <b>unlimited waiting time </b>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </button>
                                                {!isMobile && (
                                                    <div className="recommendation-list">
                                                        <span><img src={planeTakeoffBold} alt=""/></span>
                                                        <div className="recommendation-content">
                                                            <div className="recommendation-content-list">
                                                                <h3>Recommended pickup <div className="local">Your driver adjusts automatically if your flight is delayed or arrives early. No action needed</div></h3>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className={`hopn-pickup-options-grid-col gray ${isCustomActive ? 'selected' : 'section-disabled'}`}
                                        onClick={!isCustomActive ? () => setSelectedSection('custom') : undefined}
                                    >
                                        <div className="hopn-pickup-option-top hopn-pickup-option-top--custom">
                                            <button
                                                type="button"
                                                className="hopn-pickup-option-best section-radio"
                                                onClick={() => setSelectedSection('custom')}
                                                aria-pressed={isCustomActive}
                                                aria-label="Select custom pickup time"
                                            >
                                                <img src={selectedIcon} alt=""/>
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            className="hopn-pickup-info-row clickable"
                                            disabled={!isCustomActive}
                                            onClick={() => {
                                                if (isMobile) {
                                                    storage.set(STORAGE_KEYS.TDT_SHOW_WAITING_INFO, true);
                                                }
                                                props.setStep("travelingDateTime");
                                            }}
                                        >
                                            <div className="hopn-pickup-info-icon">
                                                <img src={clockIconBold} alt="Clock"/>
                                            </div>
                                            <div className="hopn-pickup-info-text">
                                                <h5>Custom pickup time</h5>
                                                <p>Choose your own time</p>
                                            </div>
                                            <span className="hopn-pickup-info-chevron">
                                                <img src={arrRight} alt=""/>
                                            </span>
                                        </button>

                                        {!isMobile && (
                                            <>
                                                <div className="hopn-pickup-info-row">
                                                    <div className="hopn-pickup-info-icon">
                                                        <img src={stopWatchBold} alt="Stopwatch"/>
                                                    </div>
                                                    <div className="hopn-pickup-info-text">
                                                        <h5>{round1(POLICY_CONSTANTS.WAITING_PICKUP_FREE_SECONDS / 60)} minutes free waiting</h5>
                                                        <p>Your driver will wait free for {round1(POLICY_CONSTANTS.WAITING_PICKUP_FREE_SECONDS / 60)} minutes after your selected pickup time</p>
                                                    </div>
                                                </div>

                                                <div className="hopn-pickup-info-row">
                                                    <div className="hopn-pickup-info-icon">
                                                        <img src={cardBold} alt="Card"/>
                                                    </div>
                                                    <div className="hopn-pickup-info-text">
                                                        <h5>After {round1(POLICY_CONSTANTS.WAITING_PICKUP_FREE_SECONDS / 60)} minutes</h5>
                                                        <p>${round1(POLICY_CONSTANTS.WAITING_PICKUP_RATE_CENTS_PER_SECOND * 60 * 60 / 100)} will be charged per hour, billed to your saved payment method</p>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <ToAirportRecommendedCard calc={calc} expanded={toAirportExpanded} onToggle={() => setToAirportExpanded(v => !v)} onLiabilityInfo={openLiability} waitingItems={waitingItems} airportBuffer={airportBuffer} arriveEarlyMinutes={arriveEarlyMinutes}/>
                        )}

                        <button
                            type="button"
                            className="hopn-add-passenger-button duble-title"
                            disabled={!!timeError}
                            onClick={handleSelectTime}
                        >
                            <div className="multiple-text">
                                <span>Confirm pickup time</span>
                                <span>{confirmDateTimeStr}</span>
                            </div>
                        </button>

                        {(!isFromAirport || isCustomActive) && (
                            <button
                                type="button"
                                className="hopn-choose-own-time-link"
                                onClick={() => {
                                    if (isMobile) {
                                        storage.set(STORAGE_KEYS.TDT_SHOW_WAITING_INFO, true);
                                    }
                                    props.setStep("travelingDateTime");
                                }}
                            >
                                I'll choose my own time
                                <img src={arrRight} alt=""/>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {isEditMode && (
                <ModalFooter>
                    <div className="hopn-submit-btn-wrapper">
                        <button
                            className="hopn-add-passenger-button no-icon"
                            disabled={!timeSelected}
                            onClick={handleDone}
                        >
                            Done
                        </button>
                    </div>
                </ModalFooter>
            )}
        </>
    );
}

export default SmartTimeSelect;
